import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/api/ux";
import { CountBadge } from "@/components/ui/CountBadge";
import type { NotificationDto } from "@/types/api";
import { WidgetFrame } from "./WidgetFrame";

/**
 * The notification bell's feed promoted to a dashboard panel ("Needs My
 * Attention"). Deliberately shares the bell's exact query key + fetcher so
 * the panel adds ZERO extra requests — the bell's 60s poll keeps both fresh.
 */
export function NeedsMyAttentionPanel() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const list = useQuery({
    queryKey: ["notifications", "feed", { unreadOnly: false, page: 1, pageSize: 20 }],
    queryFn: () => notificationsApi.list(false, 1, 20),
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "feed"] }),
  });

  const unreadCount = list.data?.unreadCount ?? 0;
  const items = (list.data?.data ?? []).slice(0, 5);

  const onEntryClick = (n: NotificationDto) => {
    if (!n.isRead) markRead.mutate(n.id);
    if (n.linkUrl) navigate(n.linkUrl);
  };

  return (
    <WidgetFrame
      title="Needs my attention"
      to="/notifications"
      isLoading={list.isLoading}
      isError={list.isError}
      isEmpty={!list.isLoading && !list.isError && items.length === 0}
      emptyText="You're all caught up."
    >
      <div className="flex items-center gap-space-2 mb-space-2">
        <CountBadge count={unreadCount} tone="error" size="sm" />
        <span className="text-label text-gray-mid uppercase tracking-label font-display">
          {unreadCount === 1 ? "1 unread" : `${unreadCount} unread`}
        </span>
      </div>
      <ul className="m-0 p-0 list-none divide-y divide-border-light">
        {items.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              onClick={() => onEntryClick(n)}
              className="w-full text-left py-space-2 hover:bg-white-warm -mx-space-2 px-space-2 rounded-sm"
            >
              <div className="flex items-start gap-space-2">
                {!n.isRead && (
                  <span
                    className="mt-[6px] h-2 w-2 rounded-pill bg-status-error shrink-0"
                    aria-label="Unread"
                  />
                )}
                <div className="min-w-0">
                  <div className="text-small font-semibold text-onyx truncate">{n.title}</div>
                  {n.body && <div className="text-label text-gray-mid truncate">{n.body}</div>}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </WidgetFrame>
  );
}
